import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Lock, Menu, X } from "lucide-react";
import { LogoMark, LogoWordmark, TikTokIcon } from "./Logo";
import { useLang } from "@/context/LanguageContext";
import { IG_PROFILE_URL, TIKTOK_URL, scrollToId } from "@/lib/constants";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-edge bg-surface/60" data-testid="language-switcher">
      {["de", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-button`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-volt text-obsidian" : "text-mutedink hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const socialBtn =
  "flex h-9 w-9 items-center justify-center border border-edge text-mutedink transition-colors duration-300 hover:border-volt hover:text-volt";

export const HeaderNav = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const links = [
    { id: "katalog", label: t.nav.shop, testid: "nav-link-shop" },
    { id: "so-gehts", label: t.nav.how, testid: "nav-link-how" },
    { id: "kontakt", label: t.nav.contact, testid: "nav-link-contact" },
  ];
  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };
  return (
    <header
      data-testid="header-nav"
      className="fixed top-0 left-0 right-0 z-50 border-b border-edge bg-[#0B1526]/90 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 md:px-8">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <LogoMark size={34} />
          <LogoWordmark />
        </button>
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-mutedink transition-colors duration-300 hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-volt transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <a data-testid="nav-tiktok-link" href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className={socialBtn} aria-label="TikTok">
            <TikTokIcon size={15} />
          </a>
          <a data-testid="nav-instagram-link" href={IG_PROFILE_URL} target="_blank" rel="noopener noreferrer" className={socialBtn} aria-label="Instagram">
            <Instagram size={16} />
          </a>
          <LangSwitch />
          <Link
            data-testid="nav-admin-link"
            to="/admin"
            className="flex h-9 w-9 items-center justify-center border border-edge text-mutedink transition-colors duration-300 hover:border-edge-bright hover:text-ink"
            aria-label={t.nav.admin}
          >
            <Lock size={14} />
          </Link>
        </div>
        <div className="flex items-center gap-2 md:hidden">
          <LangSwitch />
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-9 w-9 items-center justify-center border border-edge text-ink"
            aria-label={t.nav.menu}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-edge bg-obsidian/95 px-5 py-4 backdrop-blur-md md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-${l.testid}`}
                onClick={() => go(l.id)}
                className="border-b border-edge/60 py-3 text-left font-grotesk text-base text-ink"
              >
                {l.label}
              </button>
            ))}
            <div className="flex flex-wrap items-center gap-4 pt-3">
              <a data-testid="mobile-nav-tiktok-link" href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-mutedink">
                <TikTokIcon size={14} /> TikTok
              </a>
              <a data-testid="mobile-nav-instagram-link" href={IG_PROFILE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-mutedink">
                <Instagram size={16} /> Instagram
              </a>
              <Link data-testid="mobile-nav-admin-link" to="/admin" className="flex items-center gap-2 text-sm text-mutedink">
                <Lock size={14} /> {t.nav.admin}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
